import type { Selector } from 'reselect';

import type { ModelConfig, RootState, Slicer } from '@swf/core/store';

import effects from './effects';
import * as reducers from './reducers/betSlip';
import type { BetSlipState, PersistedBetSlipState } from './types/betSlip';

export const initialState: BetSlipState = {
  acceptedOdds: {},
  isLocked: false,
  isOpen: false,
  opportunities: {
    MULTIPLE: [],
    SINGLE: []
  },
  opportunityFailures: [],
  wallets: {},
  selections: [],
  submittedOpportunities: {
    SINGLE: [],
    MULTIPLE: []
  },
  receipt: {
    SINGLE: [],
    MULTIPLE: []
  },
  rehydrated: false
};

export const rehydratedInitialState: BetSlipState = {
  ...initialState,
  rehydrated: true
};

const selectors = (slice: Slicer<BetSlipState>) => ({
  acceptedOdds() {
    return slice((state) => state.acceptedOdds);
  },
  isLocked() {
    return slice((state) => state.isLocked);
  },
  isOpen() {
    return slice((state) => state.isOpen);
  },
  opportunities() {
    return slice((state) => state.opportunities);
  },
  singleOpportunities() {
    return slice((state) => state.opportunities.SINGLE);
  },
  multipleOpportunities() {
    return slice((state) => state.opportunities.MULTIPLE);
  },
  receipt() {
    return slice((state) => state.receipt);
  },
  persistedState(): Selector<RootState, PersistedBetSlipState> {
    return slice(({ receipt, rehydrated, isLocked, ...state }) => state);
  },
  submittedOpportunities() {
    return slice((state) => state.submittedOpportunities);
  },
  selections() {
    return slice((state) => state.selections);
  },
  wallets() {
    return slice((state) => state.wallets);
  },
  hasInvalidCombination() {
    return slice((state) =>
      state.opportunityFailures.some(
        (failure) =>
          failure.failureCode === 'INVALID_COMBINATION' ||
          failure.failureCode === 'INVALID_SGM_COMBINATION' ||
          failure.failureCode === 'IMPOSSIBLE_SAME_MARKET_COMBINATION'
      )
    );
  },
  errorMessage() {
    return slice((state) => state.errorMessage);
  },
  isAutoDismissibleError() {
    return slice((state) => state.isAutoDismissibleError);
  },
  hasRehydrated() {
    return slice((state) => state.rehydrated);
  }
});

interface BetSlipModel extends ModelConfig<BetSlipState> {
  selectors: typeof selectors;
  reducers: typeof reducers;
  effects: typeof effects;
}

const betSlip: BetSlipModel = {
  state: { ...initialState },
  selectors,
  reducers,
  effects
};

export default betSlip;
